import axios from "axios";
import qs from "qs";
import queryClient from "hooks/queryClient";
import { findTenant } from "multitenancy/findTenant";

function createClient(headers, responseType) {
    const client = axios.create({
        baseURL: "/",
        headers: headers,
        responseType: responseType,
    });

    client.interceptors.request.use(function (config) {
        const tenantIdentifier = findTenant();

        if (tenantIdentifier) {
            config.headers = config.headers ?? {};
            config.headers["X-Tenant-Id"] = tenantIdentifier;
        }

        return config;
    });

    client.interceptors.response.use(
        (response) => response,
        async (error) => {
            const response = error.response;
            const originalRequest = error.config;
            const isAuthRequest = originalRequest.url.endsWith("auth/refresh");

            if (
                response &&
                response.status === 401 &&
                !isAuthRequest &&
                !originalRequest.__isRetry
            ) {
                try {
                    await client.post(`/api/v1/auth/refresh`);
                } catch (e) {
                    console.error("Can not refresh auth token", e);
                    return Promise.reject(error);
                } finally {
                    await queryClient.invalidateQueries("context");
                }

                originalRequest.__isRetry = true;
                return axios(originalRequest);
            }

            return Promise.reject(error);
        },
    );

    return client;
}

export class ApiClientBase {
    static #defaultClient = createClient({
        Accept: "application/json",
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
    });

    getClient() {
        return ApiClientBase.#defaultClient;
    }

    get(uri, params) {
        const client = this.getClient();

        const config = {
            params: params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: "repeat" });
            },
        };

        return client.get(uri, config);
    }

    post(uri, data) {
        const client = this.getClient();
        return client.post(uri, data);
    }

    postFile(uri, data) {
        let formData = new FormData();
        formData.append("file", data.file ? data.file[0] : data);
        const client = createClient({
            Accept: "multipart/form-data",
            "Content-Type": "multipart/form-data",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
        });
        return client.post(uri, formData);
    }
}
